import React, { Suspense, lazy, useEffect, useState } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import PageContainer from './components/ui/PageContainer';
import Button from './components/ui/Button';
import EventCard from './components/EventCard';
import { usePageMeta } from './seo/usePageMeta';
import { SEARCH_PAGE } from './seo/site';
import { useFavourites } from './hooks/useFavourites';
import { useAuth } from './auth/AuthProvider';
import { validatePostcode } from './validation/validatePostcode';
import {
  BrowseFilters,
  DEFAULT_RADIUS,
  DISTANCES,
  RADII,
  SPORTS,
  STATES,
  browseEvents,
  fetchSavedPostcode,
  upcomingMonths,
} from './lib/events';
import { EventDetails } from './types';

const PAGE_SIZE = 24;
// The map shows every match at once.
const MAP_LIMIT = 500;

// Mapbox GL is large, so the map loads only when someone switches to it.
const EventsMap = lazy(() => import('./components/EventsMap'));
const FILTER_KEYS: (keyof BrowseFilters)[] = ['q', 'sport', 'state', 'month', 'distance', 'near', 'within', 'sort'];

const selectClass =
  'w-full rounded-xl border border-surface-subtle bg-white px-3 py-2.5 text-sm text-ink shadow-sm focus:border-brand-400 focus:outline-none focus:ring-4 focus:ring-brand-100';

// Browse upcoming events with filters. The filters live in the URL so a view
// can be shared or bookmarked, and the back button works.
const EventsPage: React.FC = () => {
  usePageMeta({ title: SEARCH_PAGE.title, description: SEARCH_PAGE.description, canonicalPath: SEARCH_PAGE.path });

  const [searchParams, setSearchParams] = useSearchParams();
  const filters: BrowseFilters = Object.fromEntries(
    FILTER_KEYS.map((k) => [k, searchParams.get(k) ?? '']).filter(([, v]) => v)
  );
  const page = Math.max(1, Number(searchParams.get('page')) || 1);
  const view = searchParams.get('view') === 'map' ? 'map' : 'list';
  const filtersKey = searchParams.toString();

  const [query, setQuery] = useState(filters.q ?? '');
  const [events, setEvents] = useState<EventDetails[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { favouriteIds, toggleFavourite } = useFavourites();
  const { session } = useAuth();
  const [nearInput, setNearInput] = useState(filters.near ?? '');
  const [nearError, setNearError] = useState('');

  // Keep the postcode box in step with the address (e.g. Back, Clear filters).
  useEffect(() => {
    setNearInput(filters.near ?? '');
    setNearError('');
  }, [filters.near]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    const request = view === 'map' ? browseEvents(filters, MAP_LIMIT, 0) : browseEvents(filters, PAGE_SIZE, (page - 1) * PAGE_SIZE);
    request
      .then((result) => {
        if (cancelled) return;
        setEvents(result.events);
        setTotal(result.total);
      })
      .catch((err) => {
        console.error('Error loading events:', err);
        if (cancelled) return;
        if (filters.near && axios.isAxiosError(err) && err.response?.status === 400) {
          setEvents([]);
          setTotal(0);
          setNearError(`We couldn't find the postcode ${filters.near}.`);
        } else {
          setError('Could not load events. Please try again.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // filtersKey captures every filter and the page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey]);

  // Changing a filter starts again from page 1.
  const setFilters = (changes: Partial<Record<keyof BrowseFilters, string>>) => {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(changes)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    next.delete('page');
    setSearchParams(next);
  };
  const setFilter = (key: keyof BrowseFilters, value: string) => setFilters({ [key]: value });

  // An empty postcode removes the near filter and its options.
  const applyNear = (value: string) => {
    const postcode = value.trim().toUpperCase();
    if (!postcode) {
      setFilters({ near: '', within: '', sort: '' });
      return;
    }
    const err = validatePostcode(postcode);
    setNearError(err);
    if (!err) setFilters({ near: postcode });
  };

  const applySavedPostcode = async () => {
    try {
      const postcode = await fetchSavedPostcode();
      if (postcode) applyNear(postcode);
      else setNearError('There is no postcode saved on your account yet; add one on your account page.');
    } catch (err) {
      console.error('Error loading your postcode:', err);
      setNearError('Could not load your postcode. Please type it in.');
    }
  };

  const setPage = (p: number) => {
    const next = new URLSearchParams(searchParams);
    if (p > 1) next.set('page', String(p));
    else next.delete('page');
    setSearchParams(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const clearFilters = () => {
    setQuery('');
    setSearchParams(new URLSearchParams(view === 'map' ? { view: 'map' } : {}));
  };

  const setView = (v: 'list' | 'map') => {
    const next = new URLSearchParams(searchParams);
    if (v === 'map') next.set('view', 'map');
    else next.delete('view');
    next.delete('page');
    setSearchParams(next);
  };
  const unmapped = events.filter((e) => e.Latitude == null || e.Longitude == null).length;

  const pages = Math.ceil(total / PAGE_SIZE);
  const hasFilters = FILTER_KEYS.some((k) => filters[k]);
  const within = filters.within ?? DEFAULT_RADIUS;
  const nearText = filters.near ? ` within ${within} km of ${filters.near}` : '';

  return (
    <PageContainer>
      <h1 className="mb-1 text-2xl font-bold text-ink">Find your next event</h1>
      <p className="mb-6 text-sm text-ink-muted">Triathlons, runs, swims and rides across Australia.</p>

      <div className="mb-6 rounded-2xl border border-surface-subtle bg-white p-4 shadow-card sm:p-5">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setFilter('q', query.trim());
          }}
          className="mb-3 flex gap-2"
        >
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by event, town or organiser"
            aria-label="Search events"
            className={selectClass}
          />
          <Button type="submit">Search</Button>
        </form>

        <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
          <select aria-label="Sport" value={filters.sport ?? ''} onChange={(e) => setFilter('sport', e.target.value)} className={selectClass}>
            <option value="">All sports</option>
            {SPORTS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <select aria-label="State" value={filters.state ?? ''} onChange={(e) => setFilter('state', e.target.value)} className={selectClass}>
            <option value="">All states</option>
            {STATES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <select aria-label="Month" value={filters.month ?? ''} onChange={(e) => setFilter('month', e.target.value)} className={selectClass}>
            <option value="">Any month</option>
            {upcomingMonths().map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
          <select aria-label="Distance" value={filters.distance ?? ''} onChange={(e) => setFilter('distance', e.target.value)} className={selectClass}>
            <option value="">Any distance</option>
            {DISTANCES.map((d) => (
              <option key={d.value} value={d.value}>
                {d.label}
              </option>
            ))}
          </select>
        </div>

        <div className="mt-2 grid grid-cols-2 gap-2 md:grid-cols-4">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              applyNear(nearInput);
            }}
            className="col-span-2 flex gap-2"
          >
            <input
              type="text"
              value={nearInput}
              onChange={(e) => setNearInput(e.target.value.toUpperCase())}
              placeholder="Near postcode, e.g. 2000"
              aria-label="Near postcode"
              maxLength={8}
              className={selectClass}
            />
            <Button type="submit" variant="secondary">
              Go
            </Button>
          </form>
          <select
            aria-label="Within"
            value={within}
            disabled={!filters.near}
            onChange={(e) => setFilter('within', e.target.value === DEFAULT_RADIUS ? '' : e.target.value)}
            className={`${selectClass} disabled:opacity-50`}
          >
            {RADII.map((r) => (
              <option key={r} value={r}>
                Within {r} km
              </option>
            ))}
          </select>
          <select
            aria-label="Sort"
            value={filters.near ? filters.sort ?? '' : ''}
            disabled={!filters.near}
            onChange={(e) => setFilter('sort', e.target.value)}
            className={`${selectClass} disabled:opacity-50`}
          >
            <option value="">Soonest first</option>
            <option value="distance">Closest first</option>
          </select>
        </div>
        {(nearError || (session && !filters.near)) && (
          <div className="mt-2 flex flex-wrap items-center gap-3 text-xs">
            {nearError && <p className="font-medium text-red-600">{nearError}</p>}
            {session && !filters.near && (
              <button type="button" onClick={applySavedPostcode} className="font-medium text-brand-600 hover:text-brand-700">
                Use my saved postcode
              </button>
            )}
          </div>
        )}
      </div>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3 text-sm text-ink-muted">
        <div className="inline-flex rounded-xl border border-surface-subtle bg-white p-1 shadow-sm" role="group" aria-label="View">
          {(['list', 'map'] as const).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setView(v)}
              aria-pressed={view === v}
              className={`rounded-lg px-3 py-1.5 text-sm font-medium ${
                view === v ? 'bg-brand-500 text-white' : 'text-ink-muted hover:text-ink'
              }`}
            >
              {v === 'list' ? 'List' : 'Map'}
            </button>
          ))}
        </div>
        <span aria-live="polite" className="mr-auto">
          {loading ? 'Loading events…' : `${total} upcoming ${total === 1 ? 'event' : 'events'}${nearText}`}
        </span>
        {hasFilters && (
          <button type="button" onClick={clearFilters} className="font-medium text-brand-600 hover:text-brand-700">
            Clear filters
          </button>
        )}
      </div>

      {error && <p className="mb-4 text-sm font-medium text-red-600">{error}</p>}

      {view === 'map' && !error && (events.length > 0 || loading) ? (
        <Suspense fallback={<div className="h-[70vh] min-h-[420px] w-full animate-pulse rounded-2xl bg-surface-subtle" />}>
          <EventsMap events={events} />
          {!loading && unmapped > 0 && (
            <p className="mt-2 text-xs text-ink-faint">
              {unmapped} {unmapped === 1 ? "event isn't" : "events aren't"} on the map because we don&apos;t have a location yet;
              switch to the list to see {unmapped === 1 ? 'it' : 'them'}.
            </p>
          )}
        </Suspense>
      ) : !loading && !error && events.length === 0 ? (
        <div className="rounded-2xl border border-surface-subtle bg-white p-10 text-center shadow-card">
          <p className="font-semibold text-ink">No events match those filters.</p>
          <p className="mt-1 text-sm text-ink-muted">
            {filters.near && within !== '250'
              ? 'Try a bigger distance, or clear the filters.'
              : 'Try a different month or state, or clear the filters.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" aria-busy={loading}>
          {(loading ? Array.from({ length: 6 }) : events).map((event, i) =>
            loading ? (
              <div key={i} className="h-72 animate-pulse rounded-2xl bg-surface-subtle" />
            ) : (
              <EventCard
                key={(event as EventDetails).Id}
                event={event as EventDetails}
                favourited={favouriteIds.has((event as EventDetails).Id)}
                onToggleFavourite={toggleFavourite}
              />
            )
          )}
        </div>
      )}

      {view === 'list' && pages > 1 && (
        <nav className="mt-8 flex items-center justify-center gap-3" aria-label="Pages">
          <Button variant="secondary" disabled={page <= 1} onClick={() => setPage(page - 1)}>
            ← Previous
          </Button>
          <span className="text-sm text-ink-muted">
            Page {page} of {pages}
          </span>
          <Button variant="secondary" disabled={page >= pages} onClick={() => setPage(page + 1)}>
            Next →
          </Button>
        </nav>
      )}
    </PageContainer>
  );
};

export default EventsPage;
